import React, { useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import Header from '../components/Header';
import MarketingHeader from '../components/Marketing/MarketingHeader';
import MarketingFooter from '../components/Marketing/MarketingFooter';
import LocaleRouteSync from '../components/LocaleRouteSync';
import { isBlogPath, MARKETING_PATHS, normalizeMarketingPathname } from '../config/localeRoutes';
import { FR_MARKETING_METIER_PATHS } from '../config/marketingMetierNav';
import { allMarketPaths, isMarketPath } from '../config/marketRoutes';
import './PublicLayout.css';
import { scheduleStripMarketingSeoShellAfterReactHero } from '../utils/stripMarketingSeoShell';

const METIER_LOCALIZED_PATHS = Object.values(MARKETING_PATHS)
  .filter((p): p is typeof MARKETING_PATHS.educateurCanin => typeof p === 'object' && 'fr' in p && p.fr !== '/')
  .flatMap((p) => [p.fr, p.es, p.en, p.de, p.it]);

const MARKET_PATHS = allMarketPaths();

const MARKETING_PAGES = [
  '/',
  '/es',
  '/en',
  '/de',
  '/it',
  '/offre-configuration',
  '/facturation-electronique',
  '/acaced',
  '/fonctionnalites',
  '/modele-facture-prestation-chien',
  '/modele-contrat-garde-chien',
  '/tarifs-educateur-canin-logiciel',
  '/migrer-whatsapp-excel-vers-logiciel-pro-chien',
  ...METIER_LOCALIZED_PATHS,
  ...MARKET_PATHS,
  '/blog',
  '/partenaires',
  '/v2',
  '/faq',
  '/avis-rintintin-pro',
  '/logiciel-dog-sitter',
  '/loisir-canin',
  '/ecole-club-canin-logiciel',
  // Structure pages FR : MarketingHeader + MarketingFooter (plus l'ancien Header seul)
  '/alternatives-logiciel-pro-chien',
  '/terms-of-use',
  '/privacy-policy',
];

function isMetierPath(pathname: string): boolean {
  const path = normalizeMarketingPathname(pathname);
  return (
    METIER_LOCALIZED_PATHS.includes(path) ||
    FR_MARKETING_METIER_PATHS.includes(path) ||
    isMarketPath(path)
  );
}

const PublicLayout: React.FC = () => {
  useTranslation('common');
  const location = useLocation();
  const isNative = Capacitor.isNativePlatform();
  const pathname = normalizeMarketingPathname(location.pathname);

  // Blog FR (/blog) + locales (/es/blog, /de/blog, …) — sinon pas de LocaleRouteSync
  const isBlogRoute = isBlogPath(pathname);

  // Déterminer si c'est une page marketing
  const isMarketingPage = MARKETING_PAGES.includes(pathname) ||
    pathname.startsWith('/fonctionnalites/') ||
    isBlogRoute;

  // Remonter en haut de page à chaque changement de route, ou compenser le header fixe pour les ancres.
  // Retry : les pages marketing sont lazy, le layout (mockups) grandit après le 1er paint.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const anchor = location.hash.slice(1);
    let cancelled = false;
    const timers: number[] = [];
    const later = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(fn, ms));
    };

    const scrollToAnchor = (behavior: ScrollBehavior) => {
      const el = document.getElementById(anchor);
      if (!el) return false;
      const headerHeight =
        document.querySelector('.mk-header-container')?.getBoundingClientRect().height ?? 0;
      const y = el.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
      window.scrollTo({ top: Math.max(0, y), behavior });
      return true;
    };

    const compact = window.matchMedia('(max-width: 1024px)').matches;

    const waitForEl = (attempt: number) => {
      if (cancelled) return;
      if (scrollToAnchor(attempt === 0 ? 'smooth' : 'auto')) {
        if (compact) {
          later(() => {
            if (!cancelled) scrollToAnchor('auto');
          }, 400);
          later(() => {
            if (!cancelled) scrollToAnchor('auto');
          }, 900);
          later(() => {
            if (!cancelled) scrollToAnchor('auto');
          }, 1600);
        }
        return;
      }
      if (attempt < 30) later(() => waitForEl(attempt + 1), 80);
    };

    later(() => waitForEl(0), 80);

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [location.pathname, location.hash]);

  // Avant paint : retire padding/overflow body qui décalent/clippent le header fixed.
  useLayoutEffect(() => {
    if (!isMarketingPage || isNative) return;
    document.documentElement.classList.add('mk-marketing-page');
    document.body.classList.add('mk-marketing-page');
    return () => {
      document.documentElement.classList.remove('mk-marketing-page');
      document.body.classList.remove('mk-marketing-page');
    };
  }, [isMarketingPage, isNative]);

  useLayoutEffect(() => {
    if (!isMarketingPage || isNative) return;
    scheduleStripMarketingSeoShellAfterReactHero();
  }, [isMarketingPage, isNative, pathname]);

  const isTransparentHero = pathname === '/' ||
    pathname === '/es' ||
    pathname === '/en' ||
    pathname === '/de' ||
    pathname === '/it' ||
    pathname === '/facturation-electronique' ||
    isMetierPath(pathname) ||
    pathname === '/v2' ||
    isBlogRoute;

  const marketingHeroLightText =
    pathname === '/' ||
    pathname === '/es' ||
    pathname === '/en' ||
    pathname === '/de' ||
    pathname === '/it' ||
    pathname === '/v2' ||
    pathname === '/facturation-electronique' ||
    isMetierPath(pathname) ||
    isBlogRoute;

  // Pages non-marketing (login, signup, legal, etc.) gardent l'ancien header
  const isNonMarketingPage = !isMarketingPage;

  if (isNative) {
    return (
      <div className="public-layout-content">
        <Outlet />
      </div>
    );
  }

  if (isMarketingPage) {
    return (
      <>
        <LocaleRouteSync />
        <MarketingHeader 
          transparent={isTransparentHero} 
          lightText={marketingHeroLightText} 
        />
        <div className={`public-layout-content ${!isTransparentHero ? 'with-header-space' : ''}`}>
          <Outlet />
        </div>
        <MarketingFooter />
      </>
    );
  }

  return (
    <>
      <Header transparent={false} showNavLinks={true} />
      <div className="public-layout-content with-header-space">
        <Outlet />
      </div>
    </>
  );
};

export default PublicLayout;
